/**
 * The highlight by network (app#498): when a node is highlighted, the nodes that share a network
 * with it light up in that network's color. The color is a ring round the glyph, and the fill keeps
 * meaning online or offline. A node in two networks has a ring in two arcs.
 *
 * ⚠ This module imports NOTHING, for the reason `update.ts` does not: the renderer needs a DOM.
 */

/**
 * The colors of the networks, by mode. Six, and none of them is a color with a meaning on the
 * graph: a node (cyan), a stream (teal or grey), receive (green), transmit (red), a service
 * (orange). `networkHues.test.ts` holds that, and the contrast on each mode's page.
 */
export const NETWORK_HUES = {
  light: ['#6D3FD1', '#C2267E', '#A06200', '#1E5FCC', '#7A4E2D', '#6B6B1F'],
  dark: ['#A78BFA', '#F472B6', '#FBBF24', '#60A5FA', '#C69C6D', '#D4D46A'],
} as const

const HUES = NETWORK_HUES.light.length

/**
 * The color of each network of the highlighted node, by network id.
 *
 * A network's color is its creation order in the org, over the palette, so it is the same whichever
 * node is highlighted. When two networks of this node get one color, the later one takes the next
 * free color. `memberIds` is in the order the node joined, so a node keeps its color when it joins
 * a second network. A node in more networks than the palette holds repeats a color.
 *
 * ⚠ A network that is not in `networks` (another org's) has no color: the app does not draw it.
 */
export function networkHues(
  memberIds: readonly string[],
  networks: ReadonlyArray<{ id?: string; createdAt?: string }>
): Map<string, number> {
  const ranks = new Map(
    [...networks]
      .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || (a.id ?? '').localeCompare(b.id ?? ''))
      .map((network, index) => [network.id, index])
  )
  const hues = new Map<string, number>()
  const taken = new Set<number>()
  for (const id of memberIds) {
    const rank = ranks.get(id)
    if (rank === undefined) continue
    let hue = rank % HUES
    for (let step = 0; step < HUES && taken.has(hue); step++) hue = (hue + 1) % HUES
    taken.add(hue)
    hues.set(id, hue)
  }
  return hues
}

/**
 * The arcs of a glyph as text: "0,3", one color for each arc. A string for the reason `GraphNode.marks` is one: the
 * adapter compares field by field, and a changed field draws the node again.
 */
export const encodeArcs = (arcs: readonly number[]): string | undefined => arcs.join(',') || undefined

export const decodeArcs = (text: string | undefined): number[] => (text ? text.split(',').map(Number) : [])

/** The colors a closed data center icon takes: each arc of the nodes it holds, once. */
export const mergeArcs = (texts: ReadonlyArray<string | undefined>): string | undefined =>
  encodeArcs([...new Set(texts.flatMap(decodeArcs))])

/** The gap between two arcs of one ring, in radians. */
const ARC_GAP = 0.3

/**
 * The SVG path of arc `index` of `count` on a ring, clockwise from 12 o'clock.
 *
 * ⚠ `count` is at least 2: one arc is a whole ring, which is a `<circle>`. A path that starts and
 * ends on one point draws nothing.
 */
export function arcPath(index: number, count: number, radius: number): string {
  const span = (2 * Math.PI) / count
  const start = -Math.PI / 2 + index * span + ARC_GAP / 2
  const end = start + span - ARC_GAP
  const at = (angle: number) => `${(radius * Math.cos(angle)).toFixed(2)} ${(radius * Math.sin(angle)).toFixed(2)}`
  return `M ${at(start)} A ${radius} ${radius} 0 ${span - ARC_GAP > Math.PI ? 1 : 0} 1 ${at(end)}`
}
